import axios from "axios/index";
import {userService} from "../../_services";
import {reTokenizeActions} from "./reTokenizeActions";
import {__isKeyAvailable} from "../commons/utility.helper";

/*
 * Helper Accumulator for reTokenizer
 */
export const reTokenizerHelper = {
    isTokenRefreshable,
    isRefreshTokenExpired,
    refreshTokenAndReattempt
};

// contains the list of waiting requests that will retry if the access tokens are successfully refreshed
let subscribers = [];

// flag for avoiding duplicate token refresh
let isAlreadyFetchingAccessToken = false;

/**
 * This method checks for possibility of refreshing tokens
 * if a refresh token exists AND if the error is due to expired token or not
 *
 * @param errorResponse The error response
 * @param userTokens The user auth tokens
 * @returns {boolean} flag indicating if 401 is due to token expiration
 */
function isTokenRefreshable(errorResponse, userTokens) {
    if (errorResponse.status !== 401) { return false; }
    
    const errorData = errorResponse.data;
    if (!(__isKeyAvailable(errorData, "error") && __isKeyAvailable(errorData, "error_description"))) {
        return false;
    }
    
    const error = errorData.error;
    const errorDesc = errorData.error_description.replace(/\s+/g, '').toLowerCase();
    // check if error is invalid token and contains access token in the description
    return (userTokens) && (userTokens.refreshToken) && (error === "invalid_token") && (errorDesc.includes("accesstoken"));
}

/**
 * Checks if refresh token has expired
 *
 * @param errorResponse The error response
 * @param userTokens The user auth tokens
 * @returns {*|string|boolean|string} flag indicating if 401 is due to refresh token expiration
 */
function isRefreshTokenExpired(errorResponse, userTokens) {
    if (errorResponse.status !== 401) { return false; }
    
    const errorData = errorResponse.data;
    if (!(__isKeyAvailable(errorData, "error") && __isKeyAvailable(errorData, "error_description"))) {
        return false;
    }
    
    const error = errorData.error;
    const errorDesc = errorData.error_description.replace(/\s+/g, '').toLowerCase();
    
    if(error === "unauthorized" && (errorDesc.includes("authentication"))) {
        // either no tokens or issue with token setup
        return true;
    }
    
    // check if error is invalid token and contains refresh token in the description
    return (userTokens) && (userTokens.refreshToken) && (error === "invalid_token") && (errorDesc.includes("refreshtoken"));
}

/**
 * This method refreshes the tokens and performs the failed requests with new access tokens
 * @param error the failed request
 * @returns {Promise<Promise<any>|Promise<Promise<never>|Promise<any>|undefined>>} promise clone
 */
async function refreshTokenAndReattempt(error) {
    try {
        const {response: errorResponse} = error;

        // cloning params of the failed request
        const retryOriginalRequest = new Promise(resolve => {
            // adding request to the queue
            addSubscriber(access_token => {
                errorResponse.config.headers.Authorization = 'Bearer ' + access_token;
                resolve(axios(errorResponse.config));
            });
        });

        if ( !isAlreadyFetchingAccessToken ) {
            isAlreadyFetchingAccessToken = true;
            const response = await userService.refreshLogin();
            if (!response && !response.token) {
                return Promise.reject(error);
            }

            isAlreadyFetchingAccessToken = false;
            onAccessTokenFetched(response.token);
        }
        return retryOriginalRequest;
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * This method re-attempts the failed requests after access token are refreshed
 * @param access_token The access token that was recently updated
 */
function onAccessTokenFetched(access_token) {
    // When the refresh is successful, we start retrying the requests one by one and empty the queue
    subscribers.forEach(callback => callback(access_token));
    subscribers = [];

    // also, load the actions that need to performed on each token refresh
    reTokenizeActions.launch();
}

/**
 * This function adds the given callback functions to the subscribers list
 * @param callback the callback to be processed
 */
function addSubscriber(callback) {
    subscribers.push(callback);
}
